/**
 * Scene-format extension predicates.
 *
 * Lives in its own leaf module (no plugin imports) so format plugins can
 * classify asset references without importing SceneFormatRegistry, which
 * imports the plugins and would create a cycle. The extension list must
 * stay in sync with the switch in SceneFormatRegistry.getSceneFormatConfig.
 */

import { Utility } from '../../utils/Utility';

/**
 * All supported scene-format extensions (without the dot).
 *
 * `glz` is the bundle (spec §4) and the only one of these the writer emits.
 * `tracex` and `glxf` are read-only pending retirement (#816); `trace`, `zip`
 * and `real` are legacy readers.
 */
export const SCENE_FORMAT_EXTENSIONS: readonly string[] = ['trace', 'glz', 'tracex', 'zip', 'real', 'glxf'];

/**
 * Extensions that name a glTF-family file — a plain model *or* a Trace scene
 * document. Never a scene format by extension alone: content decides
 * (migration plan D2).
 */
export const GLTF_EXTENSIONS: readonly string[] = ['gltf', 'glb'];

/** Check whether an extension names a glTF-family file (`.gltf` / `.glb`). */
export function isGltfExtension(extension: string): boolean {
    return GLTF_EXTENSIONS.includes(extension);
}

/** Check if a file extension is a supported scene format. */
export function isSceneFormat(extension: string): boolean {
    return SCENE_FORMAT_EXTENSIONS.includes(extension);
}

/**
 * Check whether a SceneObject `src` references a scene-format asset
 * (.trace/.glz/.tracex/.zip/.real/.glxf), as opposed to an asset src (glb,
 * image, video, builtin primitive).
 *
 * Only scene-format srcs materialize derived child SceneObjects when
 * loaded; asset srcs attach meshes to the same SceneObject. Used by both
 * load prioritization and export filtering so the two classifications
 * cannot diverge.
 */
export function isSceneFormatRef(src: string | undefined): boolean {
    return !!src && isSceneFormat(Utility.getFileExtensionFromSrc(src));
}

/**
 * The scene formats that predate the glTF 2.1 switch: the legacy readers
 * (`.trace`, `.zip`, `.real`) and the two read-only bundles awaiting
 * retirement (`.tracex`, `.glxf`). A scene opened from one of these is a
 * LEGACY SESSION — SCENE_ROOT_SPEC §2.3 "Scope — forward-only" keeps today's
 * import behaviour there until the scene is saved as a 2.1 folder document.
 *
 * Deleted with the legacy readers (#816), and with it every branch that reads
 * it.
 */
export const LEGACY_SCENE_FORMATS: readonly string[] = ['trace', 'zip', 'tracex', 'glxf', 'real'];

/** Whether a source-format extension names a pre-2.1 scene format (see above). */
export function isLegacySceneFormat(extension: string): boolean {
    return LEGACY_SCENE_FORMATS.includes(extension);
}
